import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowLeft, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { feedService } from '@/core/api';
import { useTelegram, useStore } from '@/core/providers';
import { LoadingSpinner, EmptyState } from '@/components';
import FeedCard from '@/features/feed/components/FeedCard';
import type { FeedItem } from '@/types';

function SearchPage() {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();
  const { settings } = useStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['search-feed', activeQuery, settings.language],
    queryFn: () =>
      feedService.searchFeed({
        q: activeQuery,
        lang: settings.language,
        limit: 50,
      }),
    enabled: activeQuery.trim().length > 0,
  });

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setActiveQuery(searchQuery.trim());
      hapticFeedback('impact');
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    setActiveQuery('');
    hapticFeedback('selection');
  };

  const handleCardClick = (item: FeedItem) => {
    hapticFeedback('selection');
    navigate(`/feed/${item.id}`, { state: { item } });
  };

  return (
    <div className="h-full flex flex-col bg-telegram-bg">
      <div
        className="pointer-events-none fixed left-1/2 z-30 -translate-x-1/2"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <div className="pointer-events-auto flex items-center gap-2 rounded-full bg-[var(--ff-surface)]/90 px-2 py-1 shadow-[0_12px_28px_rgba(0,0,0,0.08)] backdrop-blur">
          <button
            onClick={() => navigate(-1)}
            className="rounded-full border border-[var(--ff-border)] bg-[var(--ff-surface-alt)] px-3 py-2 text-sm font-semibold text-telegram-text shadow-sm hover:shadow transition"
            aria-label="Go back"
          >
            <ArrowLeft size={20} className="text-telegram-text" />
          </button>
        </div>
      </div>

      {/* Header with Search Bar */}
      <div className="px-4 pt-20">
        <div className="flex items-center gap-3">
          <div className="flex-1 relative rounded-2xl border border-[var(--ff-border)] bg-[var(--ff-surface)] p-1 shadow-[0_12px_28px_rgba(0,0,0,0.06)]">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-telegram-hint"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              placeholder="Search articles, topics, keywords..."
              className="w-full rounded-xl bg-[var(--ff-surface-alt)] pl-11 pr-12 py-3 text-telegram-text placeholder-telegram-hint focus:outline-none focus:ring-2 focus:ring-telegram-button"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={handleClear}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-telegram-hint/10 rounded-full transition-colors"
                aria-label="Clear search"
              >
                <X size={16} className="text-telegram-hint" />
              </button>
            )}
          </div>

          {searchQuery.trim() && (
            <button
              onClick={handleSearch}
              className="rounded-full bg-telegram-button px-4 py-2 font-semibold text-telegram-button-text shadow-sm hover:opacity-90 transition-opacity"
            >
              Search
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          {!activeQuery ? (
            <div className="flex flex-col items-center justify-center py-16 px-8">
              <div className="w-16 h-16 rounded-xl bg-telegram-secondary flex items-center justify-center mb-4">
                <Search size={32} className="text-telegram-hint" />
              </div>
              <h3 className="text-lg font-semibold text-telegram-text mb-2 text-center">
                Search Feed
              </h3>
              <p className="text-sm text-telegram-hint text-center max-w-sm">
                Enter keywords to search across all articles and topics in your feed
              </p>

              {/* Search Tips */}
              <div className="mt-8 w-full max-w-sm space-y-2">
                <p className="text-xs font-medium text-telegram-hint uppercase tracking-wide">
                  Search Tips
                </p>
                <div className="space-y-1.5">
                  {[
                    'Use specific keywords for better results',
                    'Search works across titles and summaries',
                    'Try searching by topic or entity name',
                  ].map((tip, index) => (
                    <div key={index} className="flex gap-2 text-xs text-telegram-hint">
                      <span>•</span>
                      <span>{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner size="lg" />
            </div>
          ) : isError ? (
            <EmptyState
              title="Search Failed"
              description="An error occurred while searching. Please try again."
              action={
                <button onClick={handleSearch} className="btn-primary">
                  Retry
                </button>
              }
            />
          ) : data && data.items.length > 0 ? (
            <>
              <div className="mb-4">
                <p className="text-sm text-telegram-hint">
                  Found {data.total} result{data.total !== 1 ? 's' : ''} for "{activeQuery}"
                </p>
              </div>
              <div className="space-y-3">
                {data.items.map((item) => (
                  <FeedCard key={item.id} item={item} onClick={() => handleCardClick(item)} />
                ))}
              </div>
            </>
          ) : (
            <EmptyState
              title="No Results"
              description={`No articles found for "${activeQuery}". Try different keywords.`}
              action={
                <button onClick={handleClear} className="btn-secondary">
                  Clear Search
                </button>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default SearchPage;
